import { useEffect, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDContext } from 'launchdarkly-js-client-sdk';
import { noop } from 'lodash';

import { isBrowser } from 'utils/misc-utils';
import useUser from 'hooks/use-user';
import useCart from 'hooks/use-cart';
import useFeatureFlags from 'hooks/use-feature-flags';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import usePersistedValue from 'hooks/use-persisted-value';
import { markLdIdentifiedDispensary, readLdContextDispensaryId } from 'src/utils/launch-darkly/identified-dispensary';
import meConsumer from 'shared/graphql/user/queries/me-consumer.gql';
import { getPersistedValue, setPersistedValue, removePersistedValue } from 'shared/utils/persisted-values';
import { MARKETING_CAMPAIGN_PARAMS } from 'shared/constants';
import { LD_ANONYMOUS_USER_KEY, LD_APPLICATION_CONTEXT } from 'src/constants/launch-darkly';
import { useSessionResetWatcher } from 'utils/session';
import type { MeConsumerQuery } from 'src/gql/graphql';
import { tracker } from 'utils/analytics';

export function useLaunchDarklyClientContext({ user, dispensary }): void {
  const FeatureFlags = useFeatureFlags();

  const userId = user?.id;
  // always default to getting the persisted value

  const client = useLDClient();
  const enterpriseId = dispensary?.retailer?.enterpriseId;
  const dispensaryId = dispensary?.id;
  const isCanaryUser = getPersistedValue(`launch-darkly-canary-user`, false);

  const userType = userId ? 'customer' : 'guestCustomer';
  // Re-identify on Session.reset() (kiosk lock screen) to drop traits merged in by identifyLDUser
  // after an order, so the next shopper does not inherit the previous guest's key and PII.
  const sessionResetCount = useSessionResetWatcher();

  useEffect(() => {
    void (async () => {
      const context: LDContext = {
        kind: `multi`,
        user: {
          kind: 'user',
          key: LD_ANONYMOUS_USER_KEY,
          type: userType,
          canary: isCanaryUser,
        },
        application: LD_APPLICATION_CONTEXT,
      };

      if (dispensaryId) {
        context[`ecomm-dispensary`] = {
          key: dispensaryId,
          // cName: dispensary.cName,
          // chain: dispensary.chain,
        };
      } else if (isBrowser() && window.reactEnv?.dispensaryId) {
        context[`ecomm-dispensary`] = {
          key: window.reactEnv.dispensaryId,
        };
      }

      if (enterpriseId) {
        context[`ecomm-enterprise`] = {
          key: enterpriseId,
        };
      } else if (isBrowser() && window.reactEnv?.enterpriseId) {
        context[`ecomm-enterprise`] = {
          key: window.reactEnv.enterpriseId,
        };
      }

      if (!client) {
        return;
      }

      tracker.setContext({ ldClient: client });

      try {
        await client.identify(context);
      } catch (error) {
        console.error('Error identifying user in LaunchDarkly:', error);
      }

      // Publish which store LD is now serving flags for. Read back from the client rather than
      // assuming it is `context`: identify responses are unordered, so another store's identify
      // can land last. Consumers that must not read another store's flag values subscribe to this,
      // because LD emits no change event when an identify swaps stores without changing a value.
      markLdIdentifiedDispensary(readLdContextDispensaryId(client));

      FeatureFlags.identify(context);
    })();
  }, [client, userType, dispensaryId, enterpriseId, isCanaryUser, FeatureFlags, sessionResetCount]);
}

export function useSetSessionUtm({ dispensary }): any {
  const dispensaryId = dispensary?.id;
  const { query = {} } = useRouter() ?? {};

  const utmData = useMemo(() => {
    const utmParameters = Object.keys(query).reduce((acc, key) => {
      if (MARKETING_CAMPAIGN_PARAMS.includes(key)) {
        acc[key] = Array.isArray(query[key]) ? query[key]?.[0] : query[key];
      }
      return acc;
    }, {});
    return utmParameters;
  }, [query]);

  useEffect(() => {
    if (!dispensaryId) {
      return;
    }

    const existingUtmSessionData = getPersistedValue('utmSessionData', null);

    if (existingUtmSessionData?.dispensaryId !== dispensaryId) {
      removePersistedValue('utmSessionData', true);
    }

    if (Object.keys(utmData).length === 0) {
      return;
    }

    const utmSessionData = {
      parameters: utmData,
      dispensaryId,
    };

    setPersistedValue('utmSessionData', utmSessionData, true);
  }, [dispensaryId, utmData]);
}

export function useUserQuery({ user, cart }): void {
  const token = usePersistedValue(user, 'token', 'access-token', false);
  const refetchRequest = useObserver(() => user.refetchRequest);
  const apolloClient = useApolloClient();

  useEffect(() => {
    async function checkUser(): Promise<void> {
      user.loading = true;

      if (token && (!user.isLoggedIn || refetchRequest)) {
        await queryMe();
      }

      if (!token && user.isLoggedIn) {
        user.logout();
        // Use clearStore instead of resetStore to avoid refetching ALL active queries at once.
        // This prevents a request flood that can hang the browser on logout.
        // Queries will refetch lazily as components need data, and exclusive content
        // updates correctly because userSegmentIds in query variables changes on logout.
        // Cache clear may fail, but logout should still proceed
        await apolloClient.clearStore().catch(noop);
      }
      user.loading = false;
    }

    async function queryMe(): Promise<void> {
      try {
        const { data } = await apolloClient.query<MeConsumerQuery>({ query: meConsumer, fetchPolicy: `no-cache` });

        user.setUser(data.meConsumer);
        user.refetchRequest = false;

        if (data.meConsumer?.profile.address && !cart.hasAddress) {
          cart.updateAddress({ location: data.meConsumer.profile.address, residentialStatus: 'VERIFIED' });
        }
      } catch (err) {
        console.error(err);
      }
      user.loading = false;
    }

    void checkUser();
  }, [apolloClient, user, token, refetchRequest, cart]);
}

export function useStorageListener({ user }): void {
  // listen for login events on other windows
  useEffect(() => {
    if (!isBrowser()) {
      // ssr
      return;
    }
    function handleStorageEvent(event): void {
      if (event.key === 'access-token') {
        user.token = JSON.parse(event.newValue);
      }
    }
    window.addEventListener('storage', handleStorageEvent);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('storage', handleStorageEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // FIXME: ENG-32714 fix hooks dependency
}

export default function useUserController(): void {
  const user = useUser();
  const cart = useCart();

  const { dispensary: viewingDispensary } = useDispensary();
  const dispensary = useObserver(() => viewingDispensary || cart.dispensary);

  usePersistedValue(user, `userDetailsFromExternalSource`);
  useUserQuery({ user, cart });
  useStorageListener({ user });
  useLaunchDarklyClientContext({ user, dispensary });
  useSetSessionUtm({ dispensary });
}
